import { api, type QueryParams } from '@/lib/api/client';
import type { I18n, MediaAsset, MediaUsage, Paginated } from '@/lib/api/types';

export const mediaApi = {
  async list(params?: QueryParams): Promise<Paginated<MediaAsset>> {
    const res = await api.getRaw<MediaAsset[]>('/admin/media', params);
    // Server sends `data: null` for an empty list; normalize to [] so the
    // Paginated<T> contract (data: T[]) holds for every consumer.
    return { data: res.data ?? [], meta: res.meta as Paginated<MediaAsset>['meta'] };
  },
  get(id: string): Promise<MediaAsset> {
    return api.get<MediaAsset>(`/admin/media/${id}`);
  },
  usages(id: string): Promise<MediaUsage[]> {
    return api.get<MediaUsage[]>(`/admin/media/${id}/usages`);
  },
  upload(input: {
    file: File;
    alt?: I18n;
    title?: I18n;
    folder?: string;
    restricted?: boolean;
    /** `transparent`: be-svet trims a PNG/SVG's transparent margins before storing it. */
    trim?: 'transparent';
  }): Promise<MediaAsset> {
    const fd = new FormData();
    fd.append('file', input.file);
    if (input.alt) fd.append('alt', JSON.stringify(input.alt));
    if (input.title) fd.append('title', JSON.stringify(input.title));
    if (input.folder) fd.append('folder', input.folder);
    fd.append('restricted', input.restricted ? 'true' : 'false');
    if (input.trim) fd.append('trim', input.trim);
    return api.upload<MediaAsset>('/admin/media', fd);
  },
  /**
   * `PATCH /admin/media/{id}`. `focal: null` resets the focal point to the centre; an
   * omitted key is left as stored. `is_restricted` is not editable here — the bucket is
   * chosen at upload — and the endpoint refuses a body that carries it.
   */
  update(id: string, body: Partial<Pick<MediaAsset, 'alt' | 'title' | 'caption' | 'credit' | 'folder' | 'tags' | 'focal'>>): Promise<MediaAsset> {
    return api.patch<MediaAsset>(`/admin/media/${id}`, body);
  },
  /**
   * `POST /admin/media/{id}/rotate` (B-R12-09): a quarter turn clockwise — 90 is
   * "Rotate right", 270 "Rotate left". The asset keeps its id, so every page that uses
   * it shows it turned; the response is the asset with its new URL, size, derivatives
   * and focal point. A 409 means another turn landed first.
   */
  rotate(id: string, degrees: 90 | 270): Promise<MediaAsset> {
    return api.post<MediaAsset>(`/admin/media/${id}/rotate`, { degrees });
  },
  remove(id: string): Promise<null> {
    return api.del(`/admin/media/${id}`);
  },
};

/** Accepted upload kinds for client pre-validation (server checks magic bytes). */
export const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'pdf', 'doc', 'docx', 'mp4', 'webm'];
